/**
 * DashboardNotFound — in-shell 404 for authenticated layouts.
 * Renders inside the DashboardLayout <Outlet> so the left nav + top bar
 * stay intact (no fall-through to the PUBLIC marketing 404 with the
 * Early Access sticker / pricing CTAs). Bilingual. Iter296.
 */
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Compass, ArrowLeft, LayoutDashboard } from 'lucide-react';

const COPY = {
  ja: {
    code: '404',
    eyebrow: 'ページが見つかりません',
    title: 'このページは存在しないか、移動しました。',
    body: 'URLにタイプミスがあるか、ページが移動した可能性があります。下記のいずれかからお進みください。',
    cta_home: 'ダッシュボードへ',
    cta_back: '前のページへ戻る',
    helpful: 'よく使うページ',
    customer: [
      { label: '概要', to: '/dashboard' },
      { label: 'レポート', to: '/dashboard/reports' },
      { label: '配信履歴', to: '/dashboard/reports/history' },
      { label: 'カード管理', to: '/dashboard/cards' },
      { label: 'MEISAI 接続状況', to: '/dashboard/meisai-health' },
      { label: '設定', to: '/dashboard/settings' },
    ],
    admin: [
      { label: 'コマンドセンター', to: '/admin' },
      { label: '顧客管理', to: '/admin/customers' },
      { label: 'スケジューラー稼働状況', to: '/admin/scheduler-health' },
      { label: 'レポート', to: '/admin/reports' },
    ],
  },
  en: {
    code: '404',
    eyebrow: 'Page not found',
    title: 'This page doesn’t exist or has moved.',
    body: 'The URL may have a typo, or the page may have been relocated. Here are a few ways to get back on track.',
    cta_home: 'Back to dashboard',
    cta_back: 'Go back one page',
    helpful: 'Popular pages',
    customer: [
      { label: 'Overview', to: '/dashboard' },
      { label: 'Reports', to: '/dashboard/reports' },
      { label: 'Delivery history', to: '/dashboard/reports/history' },
      { label: 'Cards', to: '/dashboard/cards' },
      { label: 'MEISAI connection', to: '/dashboard/meisai-health' },
      { label: 'Settings', to: '/dashboard/settings' },
    ],
    admin: [
      { label: 'Command Center', to: '/admin' },
      { label: 'Customers', to: '/admin/customers' },
      { label: 'Scheduler health', to: '/admin/scheduler-health' },
      { label: 'Reports', to: '/admin/reports' },
    ],
  },
};

export default function DashboardNotFound({ variant = 'customer' }) {
  const { lang } = useLanguage();
  const loc = useLocation();
  const t = COPY[lang === 'ja' ? 'ja' : 'en'];
  const home = variant === 'admin' ? '/admin' : '/dashboard';
  const links = variant === 'admin' ? t.admin : t.customer;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="dashboard-not-found">
      <div className="flex items-center gap-3 text-slate-400">
        <Compass className="w-8 h-8" strokeWidth={1.8} />
        <span className="text-4xl font-black tracking-tight text-slate-300">{t.code}</span>
      </div>
      <p className="mt-4 text-xs font-bold tracking-[0.2em] uppercase text-indigo-500">{t.eyebrow}</p>
      <h1 className="mt-1 text-2xl sm:text-3xl font-black text-slate-800 leading-tight">{t.title}</h1>
      <p className="mt-3 text-sm text-slate-500 max-w-xl leading-relaxed">{t.body}</p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Link
          to={home}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
          data-testid="dashboard-not-found-home"
        >
          <LayoutDashboard className="w-4 h-4" strokeWidth={2.2} />
          {t.cta_home}
        </Link>
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? window.history.back() : null)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="dashboard-not-found-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.2} />
          {t.cta_back}
        </button>
      </div>

      <div className="mt-8">
        <h2 className="text-sm font-bold text-slate-700 mb-3">{t.helpful}</h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {links.map((l, i) => (
            <li key={i}>
              <Link
                to={l.to}
                className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/70 hover:bg-indigo-50 hover:border-indigo-200 px-4 py-3 text-sm font-medium text-slate-700 transition-colors"
                data-testid={`dashboard-not-found-link-${i}`}
              >
                <span>{l.label}</span>
                <span className="text-indigo-500">→</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-6 text-[10px] font-mono text-slate-300 break-all">{loc.pathname}{loc.search}</p>
    </div>
  );
}
